import axiosInstance from "core/api/axiosInstance";

// ==================== FUNCIONES DE UTILIDAD ====================

const getAuthInfo = () => {
  try {
    const token = localStorage.getItem("token");
    const user = localStorage.getItem("user");

    return {
      hasToken: !!token,
      token: token || null,
      tokenLength: token ? token.length : 0,
      hasUser: !!user,
      userData: user ? JSON.parse(user) : null,
    };
  } catch (error) {
    console.error("Error obteniendo información de autenticación:", error);
    return {
      hasToken: false,
      token: null,
      tokenLength: 0,
      hasUser: false,
      userData: null,
    };
  }
};

const handleServiceError = (error, serviceName) => {
  console.error(`❌ Error en ${serviceName}:`, error);

  let errorMessage = `Error en ${serviceName}`;
  let errorDetails = {};

  if (error.response) {
    const backendMessage = error.response.data?.message || error.response.data?.msm || "";
    const backendError = error.response.data?.error || "";

    errorMessage =
      backendMessage ||
      backendError ||
      `Error ${error.response.status}: ${error.response.statusText || "Error desconocido"}`;

    errorDetails = {
      status: error.response.status,
      statusText: error.response.statusText,
      data: error.response.data,
      headers: error.response.headers,
    };

    if (error.response.status === 401) {
      errorMessage =
        backendMessage || "No autorizado. Su sesión ha expirado o el token es inválido.";
    } else if (error.response.status === 403) {
      errorMessage =
        backendMessage || "Acceso denegado. No tiene permisos para realizar esta acción.";
    } else if (error.response.status === 404) {
      errorMessage = "Endpoint no encontrado.";
    } else if (error.response.status >= 500) {
      errorMessage = backendMessage || "Error interno del servidor.";
    }
  } else if (error.request) {
    errorMessage = "No se pudo conectar con el servidor. Verifique su conexión a internet.";
    errorDetails = { request: error.request };
  } else {
    errorMessage = error.message || "Error de configuración de la solicitud";
    errorDetails = { message: error.message, stack: error.stack };
  }

  return {
    error: true,
    message: errorMessage,
    details: errorDetails,
    shouldLogout: false,
  };
};

// ==================== SERVICIO PARA OBTENER REPORTE DIARIO ====================

export const getDailyReportService = async (params = {}) => {
  try {
    const authInfo = getAuthInfo();
    if (!authInfo.hasToken) {
      throw new Error("No hay token de autenticación. Por favor inicie sesión.");
    }

    // Validar que ambas fechas estén presentes
    if (!params.init || !params.end) {
      throw new Error("Las fechas de inicio y fin son requeridas para obtener el reporte.");
    }

    const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
    if (!dateRegex.test(params.init) || !dateRegex.test(params.end)) {
      throw new Error("Formato de fecha inválido. Use YYYY-MM-DD.");
    }

    // Validar que end sea mayor o igual a init
    const initDate = new Date(params.init);
    const endDate = new Date(params.end);

    if (endDate < initDate) {
      throw new Error("La fecha final debe ser mayor o igual a la fecha inicial.");
    }

    const payload = {
      init: params.init,
      end: params.end,
    };

    if (
      params.box !== undefined &&
      params.box !== null &&
      params.box !== "" &&
      params.box !== "TODAS"
    ) {
      payload.box = Number(params.box);
    }

    if (params.user_kme && params.user_kme.trim() !== "" && params.user_kme !== "TODOS") {
      payload.user_kme = params.user_kme.trim();
    }

    const config = {
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        Authorization: `Bearer ${authInfo.token}`,
      },
    };

    const response = await axiosInstance.post("/api/tp/daily-report", payload, config);
    const { data, status } = response;

    if (!data) {
      throw new Error("No se recibió respuesta del servidor.");
    }

    const isSuccess = status >= 200 && status < 300;
    const responseMessage = data.msm || data.message || "";

    if (!isSuccess) {
      throw new Error(responseMessage || "Error al obtener el reporte diario");
    }

    const reportData = data.data || {};

    const processedData = {
      totals: {
        efectivo: {
          total: reportData.totalOperationsEfe || 0,
          count: reportData.countOperationsEfe || 0,
        },
        tarjeta: {
          total: reportData.totalOperationsTar || 0,
          count: reportData.countOperationsTar || 0,
        },
        totalOperations: reportData.operations || 0,
        totalFacturacion:
          (reportData.totalOperationsEfe || 0) + (reportData.totalOperationsTar || 0),
      },
      facturacion: {
        fecha: reportData.facturacion?.fecha || `${params.init} - ${params.end}`,
        primeraFactura: reportData.facturacion?.primera_factura || null,
        primeraFecha: reportData.facturacion?.primera_fecha || null,
        ultimaFactura: reportData.facturacion?.ultima_factura || null,
        ultimaFecha: reportData.facturacion?.ultima_fecha || null,
      },
      facturacionDesc: {
        init: {
          fecha: reportData.facturacionDesc?.init?.fecha || null,
          prefijoFactura: reportData.facturacionDesc?.init?.prefijo_factura || null,
          numeroFactura: reportData.facturacionDesc?.init?.numero_factura || null,
          fechaHoraCompleta: reportData.facturacionDesc?.init?.fecha_hora_completa || null,
          statusDescripcion: reportData.facturacionDesc?.init?.status_descripcion || null,
          statusOriginal: reportData.facturacionDesc?.init?.status_original || null,
        },
        end: {
          fecha: reportData.facturacionDesc?.end?.fecha || null,
          prefijoFactura: reportData.facturacionDesc?.end?.prefijo_factura || null,
          numeroFactura: reportData.facturacionDesc?.end?.numero_factura || null,
          fechaHoraCompleta: reportData.facturacionDesc?.end?.fecha_hora_completa || null,
          statusDescripcion: reportData.facturacionDesc?.end?.status_descripcion || null,
          statusOriginal: reportData.facturacionDesc?.end?.status_original || null,
        },
      },
      estadoContable: {
        actual: reportData.estadoContableActual || 0,
        anterior: reportData.estadoContableAnterior || 0,
        diferencia:
          (reportData.estadoContableActual || 0) - (reportData.estadoContableAnterior || 0),
      },
      movimientos: {
        depositos: reportData.depositos || 0,
        miselaneos: reportData.miselaneos || 0,
        recargasMensuales: reportData.recargasMensuales || 0,
        descuentosTarjeta: reportData.descuentosTarjeta || 0,
        descuentos: reportData.descuentos || 0,
        totalDescuentos: reportData.totalDescuentos || 0,
        totalDepositos: reportData.totalDepositos || 0,
      },
      turn: reportData.turn || 0,
      raw: reportData,
    };

    return {
      success: true,
      data: processedData,
      rawResponse: data,
      message: responseMessage || "Reporte diario obtenido exitosamente",
      init: params.init,
      end: params.end,
      box: params.box || null,
      user_kme: params.user_kme || null,
      timestamp: new Date().toISOString(),
      responseStatus: status,
    };
  } catch (error) {
    console.error("❌ Error en getDailyReportService:", {
      message: error.message,
      response: error.response?.data,
      status: error.response?.status,
      params: params,
    });

    const errorResult = handleServiceError(error, "getDailyReportService");

    return {
      success: false,
      error: errorResult.message,
      details: errorResult.details,
      shouldLogout: false,
      timestamp: new Date().toISOString(),
    };
  }
};

// ==================== SERVICIO PARA USUARIOS ====================

export const getDailyReportUsersService = async () => {
  try {
    const authInfo = getAuthInfo();
    if (!authInfo.hasToken) {
      throw new Error("No hay token de autenticación. Por favor inicie sesión.");
    }

    const config = {
      headers: {
        Authorization: `Bearer ${authInfo.token}`,
      },
      timeout: 15000,
    };

    const response = await axiosInstance.get("/api/tp/get-user-kme", config);
    const responseData = response.data;

    if (!responseData || response.status !== 200) {
      throw new Error("No se pudieron obtener los usuarios");
    }

    let users = [];

    if (responseData?.data?.users && Array.isArray(responseData.data.users)) {
      users = responseData.data.users
        .filter((item) => item.Usuario && item.Usuario.trim() !== "")
        .map((item) => ({
          id: item.Usuario,
          name: item.Usuario,
          label: item.Usuario,
          value: item.Usuario,
        }));
    } else if (responseData?.data && Array.isArray(responseData.data)) {
      users = responseData.data
        .filter((item) => item.Usuario && item.Usuario.trim() !== "")
        .map((item) => ({
          id: item.Usuario,
          name: item.Usuario,
          label: item.Usuario,
          value: item.Usuario,
        }));
    } else if (responseData?.users && Array.isArray(responseData.users)) {
      users = responseData.users
        .filter((item) => item.Usuario && item.Usuario.trim() !== "")
        .map((item) => ({
          id: item.Usuario,
          name: item.Usuario,
          label: item.Usuario,
          value: item.Usuario,
        }));
    }

    users.sort((a, b) => a.name.localeCompare(b.name));

    const allUsersOption = {
      id: "TODOS",
      name: "TODOS",
      label: "TODOS",
      value: "TODOS",
    };

    const result = {
      success: true,
      data: [allUsersOption, ...users],
      total: users.length,
      message: "Usuarios obtenidos exitosamente",
      timestamp: new Date().toISOString(),
    };

    return result;
  } catch (error) {
    console.error("❌ Error en getDailyReportUsersService:", error);
    const errorResult = handleServiceError(error, "getDailyReportUsersService");

    return {
      success: false,
      data: [{ id: "TODOS", name: "TODOS", label: "TODOS", value: "TODOS" }],
      error: errorResult.message,
      details: errorResult.details,
      timestamp: new Date().toISOString(),
    };
  }
};

// ==================== SERVICIO PARA CAJAS ====================

export const getDailyReportBoxesService = async () => {
  try {
    const authInfo = getAuthInfo();
    if (!authInfo.hasToken) {
      throw new Error("No hay token de autenticación. Por favor inicie sesión.");
    }

    const config = {
      headers: {
        Authorization: `Bearer ${authInfo.token}`,
      },
      timeout: 15000,
    };

    const response = await axiosInstance.get("/api/tp/get-user-kme", config);
    const responseData = response.data;

    if (!responseData || response.status !== 200) {
      throw new Error("No se pudieron obtener las cajas");
    }

    let boxes = [];

    if (responseData?.data?.box && Array.isArray(responseData.data.box)) {
      boxes = responseData.data.box
        .filter((item) => item.NumeroCaja !== undefined && item.NumeroCaja !== null)
        .map((item) => ({
          id: item.NumeroCaja,
          name: `Caja ${item.NumeroCaja}`,
          label: `Caja ${item.NumeroCaja}`,
          value: item.NumeroCaja,
        }));
    } else if (responseData?.box && Array.isArray(responseData.box)) {
      boxes = responseData.box
        .filter((item) => item.NumeroCaja !== undefined && item.NumeroCaja !== null)
        .map((item) => ({
          id: item.NumeroCaja,
          name: `Caja ${item.NumeroCaja}`,
          label: `Caja ${item.NumeroCaja}`,
          value: item.NumeroCaja,
        }));
    }

    boxes.sort((a, b) => a.id - b.id);

    const allBoxesOption = {
      id: "TODAS",
      name: "TODAS",
      label: "TODAS",
      value: "TODAS",
    };

    const result = {
      success: true,
      data: [allBoxesOption, ...boxes],
      total: boxes.length,
      message:
        boxes.length > 0 ? "Cajas obtenidas exitosamente" : "No se encontraron cajas disponibles",
      timestamp: new Date().toISOString(),
    };

    return result;
  } catch (error) {
    console.error("❌ Error en getDailyReportBoxesService:", error);
    const errorResult = handleServiceError(error, "getDailyReportBoxesService");

    return {
      success: false,
      data: [{ id: "TODAS", name: "TODAS", label: "TODAS", value: "TODAS" }],
      error: errorResult.message,
      details: errorResult.details,
      timestamp: new Date().toISOString(),
    };
  }
};

// ==================== EXPORT ====================

export default {
  getDailyReportService,
  getDailyReportUsersService,
  getDailyReportBoxesService,
};
